<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from 'vue';
import Decimal from 'decimal.js';
import { throttle } from 'lodash';
import { AgTableClient } from '@/modules/common/components/ag-table';
import * as cols from '@/modules/common/components/ag-table/columns/counterparty-credit';
import { ColDef } from 'ag-grid-enterprise';
import { CounterpartyCredit, normalizeCounterpartyCredit } from '@/utils/api/credits';
import axios from 'axios';
import logger from '@/modules/common/services/logger.service';
import { useSocketStore } from '@/store/socket-store';

const zero = new Decimal(0);

interface Totals {
  totalBilateral: Decimal;
  totalInSFT: Decimal;
}

function newTotals(): Totals {
  return {
    totalBilateral: zero,
    totalInSFT: zero,
  };
}

const socketStore = useSocketStore();

const throttledFetch = throttle(
  async () => {
    isLoading.value = true;
    try {
      await fetchTraderAdminCounterpartyCredits();
    } catch (e) {
      logger.warn(e);
    } finally {
      isLoading.value = false;
    }
  },
  500,
  {
    leading: true,
    trailing: true, // because we want the most recent data
  }
);

socketStore.useEventHandler('refreshLenderLoan', async () => {
  await throttledFetch();
});

socketStore.useEventHandler('refreshBorrowerLoan', async () => {
  await throttledFetch();
});

socketStore.useEventHandler('refreshBrokerLoan', async () => {
  await throttledFetch();
});

// Local state
const counterpartyCredits = shallowRef<CounterpartyCredit[]>([]);
const selectedItems = shallowRef<CounterpartyCredit[]>([]);
const isLoading = ref(true);

const summary = computed<CounterpartyCredit[]>(() => [
  totals.value as unknown as CounterpartyCredit,
]);

// const totalBilateral = computed<Decimal>(() => totals.value.totalBilateral);

// const totalSFTs = computed<Decimal>(() => totals.value.totalInSFT);

// const grandTotal = computed<Decimal>(() =>
//   totals.value.totalBilateral.add(totals.value.totalInSFT)
// );

const totals = computed<Totals>(() => {
  const t = newTotals();

  counterpartyCredits.value.forEach((counterparty) => {
    t.totalBilateral = t.totalBilateral.add(counterparty.totalBilateral);
    t.totalInSFT = t.totalInSFT.add(counterparty.totalInSFT);
  });

  return t;
});

const columnDefs = computed<ColDef[]>(() => [
  cols.companyDisplay(),
  // cols.totalBilateral(),
  cols.totalInSFT(),
  // cols.grandTotal(),
]);

async function fetchTraderAdminCounterpartyCredits(): Promise<void> {
  const { data } = await axios.get('/api/1/trader-admin/counterparty-credits');
  data.credits.forEach((cc: CounterpartyCredit) => normalizeCounterpartyCredit(cc));
  counterpartyCredits.value = data.credits;
}

function getRowId(counterpartyCredit: CounterpartyCredit): string {
  return counterpartyCredit.companyId;
}

onMounted(async () => {
  await throttledFetch();
});
</script>

<template>
  <VCard class="flex d-flex flex-column fill-height">
    <VCardTitle class="mb-4">
      <span class="headline">{{ $t('adminCounterpartyCredits.title') }}</span>
    </VCardTitle>
    <VCardSubtitle>
      <span class="text--secondary headline-2">
        {{ $t('adminCounterpartyCredits.description') }}
      </span>
    </VCardSubtitle>
    <VCardText v-if="isLoading" class="text--primary">
      <VContainer class="flex-column justify-center fill-height">
        <VProgressLinear color="primary" indeterminate></VProgressLinear>
        <h3 class="mt-4">Loading credits ...</h3>
      </VContainer>
    </VCardText>
    <template v-else>
      <AgTableClient
        :column-defs="columnDefs"
        :get-row-id="getRowId"
        :page-size="1000"
        :row-data="counterpartyCredits"
        :selected-items.sync="selectedItems"
        :sort="{ colId: 'totalInSFT', sort: 'desc' }"
        :summary="summary"
      />
    </template>
  </VCard>
</template>

<style lang="scss" scoped></style>
